import { Avatar, Box, styled } from '@material-ui/core';
import useCallStatusFlags from '@src/hooks/call/useCallStatusFlags';
import { EventType, MediaType } from '@src/model/frontendmodel';
import { ChatHistoryItem } from '@src/reducers/chatHistory';
import dayjs from 'dayjs';
import { useMemo, useRef } from 'react';
import { FormattedDate, FormattedMessage } from 'react-intl';
import Sticky from 'react-sticky-el';
import { CSSTransition } from 'react-transition-group';
import ContactPhoneLinkedEvent from '@src/components/ChatTimeline/ContactPhoneLinkedEvent/ContactPhoneLinkedEvent';
import { useChatHistoryContext } from '../ChatHistoryContext';

type ChatHistoryItemSeparatorProps = {
  prev?: ChatHistoryItem;
  current: ChatHistoryItem;
  next?: ChatHistoryItem;
  isScrolling?: boolean;
  completed: boolean;
  firstItemIndex: number;
  children: React.ReactNode;
};

const mediaTypes = Object.keys(MediaType);

const ContainerStyled = styled('div')({
  position: 'relative',
  '&:before': {
    transition: 'background 500ms ease-in',
    content: '""',
    position: 'absolute',
    inset: '-4px 8px 2px 8px',
    borderRadius: '8px',
  },
  '&.highlight-enter:before': {
    background: '#3C347D24',
  },
  '&.highlight-exit:before': {
    background: '#3C347D2400',
  },
});

const ChatHistoryItemSeparator: React.FC<ChatHistoryItemSeparatorProps> = ({
  prev,
  current,
  next,
  children,
  isScrolling = false,
}) => {
  const { chat, event } = current;

  const callStatus = useCallStatusFlags(event?.call?.status);
  const callStatusIgnore =
    callStatus.isCallWaitingAgent ||
    callStatus.isCallWaitingAnswer ||
    callStatus.isCallOngoing;
  const isFirstEvent = useMemo(() => !prev?.chat?.id && chat.id, [chat, prev]);
  const isLastEvent = useMemo(() => chat.id !== next?.chat.id, [chat, next]);
  const isChatClosed = useMemo(
    () =>
      ['CLOSED', 'COMPLETED_POLL', 'ABANDONED_POLL'].includes(
        chat.status as string
      ),
    [chat]
  );
  const isOldestEvent = useMemo(() => isFirstEvent, [isFirstEvent]);

  const isDayBreak = useMemo(() => {
    const eventTime = event?.sendingTime || event?.timestamp;
    const prevEventTime = prev?.event?.sendingTime || prev?.event?.timestamp;

    return (
      !!eventTime &&
      !!prevEventTime &&
      !dayjs(eventTime).isSame(prevEventTime, 'day')
    );
  }, [event, prev]);

  const refEl = useRef<HTMLDivElement>(null);

  const { messageFocused, blurMessage } = useChatHistoryContext(id => {
    if (id === event.messageId) {
      refEl.current?.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  });

  return (
    <CSSTransition
      in={messageFocused === event.messageId}
      timeout={500}
      nodeRef={refEl}
      classNames="highlight"
      onEntered={blurMessage}
      mountOnEnter={false}
      unmountOnExit={false}
    >
      <ContainerStyled ref={refEl} className="ChatItemSeparator">
        <>
          {(isDayBreak || isOldestEvent) && (
            <Sticky
              topOffset={20}
              wrapperClassName="DateSeparator-sticky-container"
              stickyClassName="DateSeparator-sticky"
              scrollElement="#chat-container"
              disabled={!isScrolling}
            >
              <div className="DateSeparator">
                <span>
                  <FormattedDate
                    value={event?.sendingTime}
                    weekday="short"
                    year="numeric"
                    month="short"
                    day="numeric"
                  />
                </span>
              </div>
            </Sticky>
          )}
          <div className="ChatItemSeparator">
            {/* Note: Ignore this event.
          This isn't longer used to be displayed on the timeline, but the event it's still arriving from the backend */}
            {event.type === 'AGENT_REQUESTS_RESUME_CHAT' && null}
            {mediaTypes.includes(event.mediaType as MediaType) &&
              !callStatusIgnore && (
                <Box
                  mx={2}
                  display="flex"
                  justifyContent={
                    event.direction === 'INCOMING' ? 'flex-start' : 'flex-end'
                  }
                >
                  {children}
                  {next?.chat.assignedTo?.avatarUrl !==
                    chat.assignedTo?.avatarUrl && (
                    <Box ml={1}>
                      <Avatar
                        className="AgentAvatar"
                        src={chat.assignedTo?.avatarUrl}
                        style={{ width: 25, height: 25 }}
                      />
                    </Box>
                  )}
                </Box>
              )}
            {(event.type === 'TRANSFER' || (isLastEvent && isChatClosed)) && (
              <Box
                display="flex"
                flexWrap="nowrap"
                alignItems="center"
                className="AssignedToAgentBox"
                mx={2}
              >
                <Avatar
                  className="AgentAvatar"
                  src={chat.assignedTo?.avatarUrl}
                  style={{ width: 25, height: 25 }}
                />
                <Box component="span" mx={1}>
                  <FormattedMessage id="ContactChatHistory.AgentNameLabel" />:{' '}
                  <span>{chat.assignedTo?.fullName}</span>
                </Box>
              </Box>
            )}
            {isLastEvent &&
              chat.status === 'CLOSED' &&
              ![EventType.CHAT_TAKEN.id, EventType.CHAT_ASSIGNED.id].includes(
                event.type
              ) && (
                <>
                  {event.type === 'TRANSFER' ? (
                    <div className="ClosedSeparator">
                      <FormattedMessage
                        id="ChatEvent.ChatXferred"
                        values={{
                          agentName: event?.toAgentUsername,
                        }}
                      />
                    </div>
                  ) : (
                    <div className="ClosedSeparator">
                      <FormattedMessage id="ContactChatHistory.ClosedChatLabel" />
                    </div>
                  )}
                </>
              )}
            {isLastEvent &&
              ['ABANDONED_POLL', 'COMPLETING_POLL'].includes(
                chat.status as string
              ) && (
                <div className="ClosedSeparator">
                  <FormattedMessage id="ContactChatHistory.AbandonedPollCloseChatLabel" />
                </div>
              )}
            {isLastEvent && chat.status === 'COMPLETED_POLL' && (
              <div className="ClosedSeparator">
                <FormattedMessage id="ContactChatHistory.CompletedPollCloseChatLabel" />
              </div>
            )}
            {isLastEvent &&
              ['LEFT_BY_CONTACT', 'CLOSED_BY_CONTACT'].includes(
                chat?.status as string
              ) && (
                <>
                  <div className="ClosedSeparator">
                    <FormattedMessage id="ChatEvent.ContactClosedChat" />
                  </div>
                </>
              )}
            {event.type === EventType.CHAT_TAKEN.id && (
              <Box
                display="flex"
                flexWrap="nowrap"
                alignItems="center"
                className="AssignedToAgentBox"
                mx={2}
                py={1}
              >
                <div className="ClosedSeparator">
                  <FormattedMessage id="ContactChatHistory.TakenChatLabel" />:{' '}
                  <span>{event.agent?.fullName}</span>
                </div>
              </Box>
            )}
            {event?.type === EventType.CHAT_ASSIGNED.id && (
              <div className="ClosedSeparator">
                <FormattedMessage
                  id="ChatEvent.ChatAssigned"
                  values={{
                    agentFromName: event?.agentWhoAssigned?.fullName,
                    agentToName: event?.agentToAssign?.fullName,
                  }}
                />
              </div>
            )}
            {event?.type === EventType.CONTACT_PHONE_LINKED.id &&
              event.phone && (
                <ContactPhoneLinkedEvent
                  contactName={event.contactName}
                  phone={event.phone}
                  timestamp={event.sendingTime ?? event.timestamp}
                />
              )}
            <div className="ChatItemSeparatorBreak" />
          </div>
        </>
      </ContainerStyled>
    </CSSTransition>
  );
};

export default ChatHistoryItemSeparator;
